import constate from 'constate';
import { BaseFlowBlockEventDefinitionSchema } from 'flow-schema';
import { useMemo } from 'react';
import { z } from 'zod';

import { api, isDefined } from 'common';

import { isPlaceholderBlockNode } from '../../../types';
import { useReactFlowViewContext } from '../../context/context';

const { IdentifierNameSchema, reservedBlockNames } = api.FlowDefinition;

const zodJsonStringSchema = z
  .string()
  .optional()
  .transform((value) => {
    return value === '' ? undefined : value;
  })
  .superRefine((value, ctx) => {
    if (value) {
      try {
        JSON.parse(value);
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: String(error),
        });
      }
    }
  });

interface UseSelectedNodeSchemaProps {
  selectedNodeSchema?: z.ZodSchema;
}

function useSelectedNodeSchema(props: UseSelectedNodeSchemaProps) {
  const { selectedNodeSchema } = props;
  const { blockNodes, selectedNode } = useReactFlowViewContext();

  const isConfigurableNode = isDefined(selectedNode) && !isPlaceholderBlockNode(selectedNode);

  const schema = useMemo(() => {
    if (!isConfigurableNode || !isDefined(selectedNodeSchema)) {
      return undefined;
    }

    return z.object({
      label: IdentifierNameSchema.refine(
        (value) => !reservedBlockNames.includes(value),
        'Name cannot be a reserved word'
      ).refine(
        (value) =>
          !blockNodes.some((node) => node.id !== selectedNode.id && node.data.label === value),
        'Name must be unique'
      ),
      description: z.string().optional(),
      mockDataEnabled: z.boolean().optional(),
      mockData: zodJsonStringSchema,
      config: selectedNodeSchema,
      events: z.array(BaseFlowBlockEventDefinitionSchema).optional(),
    });
  }, [isConfigurableNode, blockNodes, selectedNode, selectedNodeSchema]);

  const selectedNodeErrors = useMemo(() => {
    if (!isConfigurableNode || !isDefined(schema)) {
      return undefined;
    }

    try {
      schema.parse(selectedNode.data);
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues;
      }
    }
  }, [isConfigurableNode, schema, selectedNode]);

  return {
    schema,
    selectedNodeErrors,
  };
}

export const [SelectedNodeSchemaProvider, useSelectedNodeSchemaContext] =
  constate(useSelectedNodeSchema);
